import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { StyleContext } from "../providers/StyleContextProvider";
import { RecruiterMenu } from "../components/Shared/RecruiterMenu";
import { Button } from "../components/Shared/Buttons/Button";
import { ReactComponent as LeftArrow } from "../assets/svg/left_arrow.svg";
import { ReactComponent as LeftChevron } from "../assets/svg/left-chevron.svg";
import { ReactComponent as RightChevron } from "../assets/svg/right-chevron.svg";
import { ReactComponent as CheckCircle } from "../assets/svg/check-circle.svg";
import { ReactComponent as CheckCircleDisabled } from "../assets/svg/check-circle-disabled.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { ForRecruitersTexts as Texts,  CheckoutCVPackagesTexts as CheckoutTexts } from '../helpers/LayoutTexts';
import { Switch } from "../components/Shared/Buttons/Switch";
import Slider from "react-slick";
import { PackageDetail } from "../models/PackageDetail";
import { getPackageDetails, getPromoCodeValidation, getRecruiterQuotaDetails } from "../services/RecruiterService";
import useErrorPage from "../hooks/useErrorPage";
import { ReactComponent as CloseCircle } from '../assets/svg/close-circle.svg';
import { CheckboxInput } from '../components/Shared/Inputs/CheckboxInput';
import { PaymentIcons } from '../helpers/LayoutTexts';
import { PopUpWindow } from '../components/Shared/PopUpWindow';
import { SingleSelect } from "../components/Shared/SingleSelect";
import { SelectInputs } from "../models/SelectInputs";
import { LoggedRecruiter } from "../models/LoggedRecruiter";
import { AuthContext } from "../providers/AuthContextProvider";
import { TextInput } from "../components/Shared/Inputs/TextInput";
import { CheckoutModel, PaymentDetailsValidation } from "../models/CheckoutModel";
import { PromoCode } from "../models/PromoCode";
import { postCheckoutRequest } from "../services/CheckoutService";
import { WebXPayModel } from "../models/WebXPayModel";
import { CheckoutTypes } from "../enums/CheckoutTypes";
import $ from 'jquery'
import { useAlert } from "../hooks/useAlert";
import { ReactComponent as EmptyCart } from '../assets/svg/empty-cart.svg';
import { Footer } from "../components/Shared/Footer";
import { ProfileEntrance } from "../components/Home/ProfileEntrance";
import { UserType } from "../enums/UserType";
import { TableLoader } from "../components/Loaders/TableLoader";
import { validateAddress, validateTelephoneNumber } from "../helpers/Common";

type CheckoutState = {
    selectedPackage: PackageDetail | null;
    months: number;
    discount: number | undefined;
    isPromoInvalid: boolean;
    promoMessage: string;
}

interface StateProps {
    checkoutType: number;   
}

const intialFormInputs: CheckoutModel = {     
    fullName: "",
    email: "",
    promoCode: ""
}

const paymentDetailsValidation: PaymentDetailsValidation = {
    telephone: {},
    tinNumber: {},
    billingAddress: {}
}

const initialCheckoutState: CheckoutState = {
    selectedPackage: null,
    months: 1,
    discount: undefined,
    isPromoInvalid: false,
    promoMessage: ""
}

const hundredCVPackageId = 3;
const annualPackageId = 7;
const unlimitedCVPackageId = 6

export const CVPackageCheckoutPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const styleContext = useContext(StyleContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("cvPackageCheckoutPage"));
    const authContext = useContext(AuthContext);
    const recruiter: LoggedRecruiter | null = authContext.getLoggedRecruiter();            
    const [isUnlimited, setIsUnlimited] = useState(false);
    const [loading, setLoading] = useState(true);
    const [packageDetails, setPackageDetails] = useState<PackageDetail[]>([]);
    const [filteredPackageDetails, setFilteredPackageDetails] = useState<PackageDetail[]>([]);
    const [hoveredIndex, setHoveredIndex] = useState<number>(-1);    
    const [openMessageDialog, setOpenMessageDialog] = useState<boolean>(false);
    const [dialogContent, setDialogContent] = useState<any>();
    const [privacyPolicy, setPrivacyPolicy] = useState<boolean>(true);
    const [checkout, setCheckout] = useState<CheckoutModel>(intialFormInputs);
    const [checkoutState, setCheckoutState] = useState<CheckoutState>(initialCheckoutState);
    const [discountPercentage, setDiscountPercentage] = useState<number>(0);
    const [vatAmount, setVatAmount] = useState<number>(0);
    const [price, setPrice] = useState<number>(0);
    const [validation, setValidation] = useState<PaymentDetailsValidation>(paymentDetailsValidation);
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [toErrorPage] = useErrorPage();
    const { Alert, openAlert } = useAlert(); 
    const packageSliderRef = useRef<Slider | null>(null);     
    const subscriptionRef = useRef<HTMLDivElement>(null);
    const state = location.state as StateProps;
    
    let basePrice = useMemo(() => {
        const pkg = checkoutState.selectedPackage;
        if (pkg && pkg.cvLimiit != null) {
            if (pkg.cvLimiit > 0) {
                return pkg.price;
            } else {
                return pkg.price * checkoutState.months;
            }
        }
        return 0;
    }, [checkoutState.selectedPackage, checkoutState.months]);

    useEffect(() => {
        const netPrice = checkoutState.discount ? basePrice - checkoutState.discount : basePrice;
        const vat = netPrice * (Number(process.env.REACT_APP_VAT_RATE) / 100);
        setVatAmount(vat);
        setPrice(netPrice + vat);
    }, [basePrice, checkoutState.discount]);

    useLayoutEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
        window.scrollTo(0, 0);
        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                window.location.reload();
            }
        };
        window.addEventListener("pageshow", handlePageShow);
        return () => {
            window.removeEventListener("pageshow", handlePageShow);
            if ('scrollRestoration' in window.history) {
                window.history.scrollRestoration = 'auto';
            }
        };
    }, []);

    useEffect(() => {            
        setStyles(styleContext.getComponentStyle("cvPackageCheckoutPage"));
    }, [isMobile]);

    useEffect(() => {        
        getPackageDetailList();
        if(recruiter !== null) { 
            checkPendingInvoice();
            let tempFormInputs: CheckoutModel = {  
                recruiterId: recruiter.recruiterId,   
                fullName: recruiter.forename + " " + recruiter.surname,
                email: recruiter.email,
                promoCode: "",
            }
            setCheckout(tempFormInputs);
        }
    }, []);

    useEffect(() => {           
        handlePackageType();
    }, [isUnlimited]);

    const monthsCountSelectInputs: SelectInputs = {
        default: "1",
        options: [            
            {"value": 1, "option": "1"},
            {"value": 2, "option": "2"},
            {"value": 3, "option": "3"},
            {"value": 4, "option": "4"},
            {"value": 5, "option": "5"},
            {"value": 6, "option": "6"},
            {"value": 7, "option": "7"},
            {"value": 8, "option": "8"},
            {"value": 9, "option": "9"},
            {"value": 10, "option": "10"},
            {"value": 11, "option": "11"},
            {"value": 12, "option": "12"}
        ]
    }

    const sliderSettings = {
        className: "center",
        centerMode: true,
        infinite: true,
        centerPadding: "0%",
        dots: true,         
        rows: 1,
        speed: 1000,
        autoplay: false,
        autoplaySpeed: 4000,
        pauseOnHover: true,
        slidesToShow: 1,
        slidesToScroll: 1,
        slidesPerRow: 1,
        useTransform: false,
        arrows: false,
        appendDots: (dots: any) => (
            <div style={styles.dotsWrapper}>
                <ul style={styles.dots}>{dots}</ul>
            </div>
        )
    };

    const previousPackage = () => {
        packageSliderRef.current?.slickPrev();
    }

    const nextPackage = () => {
        packageSliderRef.current?.slickNext();
    }

    const handleStateUpdate = (stateUpdate: Partial<CheckoutState>) => {
        setCheckoutState(prev => ({ ...prev, ...stateUpdate}));
    }

    const handleSelectPackage = (packageDetail: PackageDetail) => {
        if (packageDetail.packageId === annualPackageId) {
          navigate('/contact');
        } 
        else {
            handleStateUpdate({
                selectedPackage: packageDetail,
                discount: undefined,
                isPromoInvalid: false,
                months: 1
            });
            setDiscountPercentage(0);
            setCheckout(prev => ({ ...prev, promoCode: "" }));
            subscriptionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const toDeposit = () => {
        let content = <>
            <div style={styles.dialogTitle}>{CheckoutTexts.title}</div>
            <div style={styles.dialogMessage}>
                {isMobile ? (
                    <ul>
                        {CheckoutTexts.accountDetails.map((item) => (
                            <li key={item.id} style={styles.messageList}>{item.detail}</li>
                        ))}
                    </ul> 
                ) : (
                    <div> 
                        {CheckoutTexts.accountDetails.map((item) => (
                            <div key={item.id} style={styles.messageList}>{item.detail}</div>
                        ))}
                    </div>
                )}                
            </div> 
            <p style={styles.footer}>{CheckoutTexts.footer}</p>
        </>
        setDialogContent(content);
        setOpenMessageDialog(true);
    }

    const handleTermsClick = (event: React.MouseEvent) => {
        event.preventDefault();
        let content = <>
            <div style={styles.termsConditions}>
                {CheckoutTexts.termsConditions.map((line, index) => (
                    <div key={index}>{line}</div>
                ))}
            </div>
        </>
        setDialogContent(content);
        setOpenMessageDialog(true);
    }
    
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setCheckout((values: any) => ({ ...values, [name]: value }));
        if (name === 'promoCode' && value === "") {
            handleStateUpdate({isPromoInvalid: false, promoMessage: ""});
        }
    }

    const handleMonthsChange = (value: number) => {
        const newMonths = (!isNaN(value) && value > 0) ? value : 1;
        const updatedPrice = checkoutState.selectedPackage?.price != null 
            ? checkoutState.selectedPackage.price * newMonths 
            : 0;
        const updatedDiscount = discountPercentage > 0
            ? (updatedPrice * discountPercentage) / 100
            : undefined;
        handleStateUpdate({ months: newMonths, discount: updatedDiscount });
    };

    const handlePackageType = () => {
        if(!isUnlimited){
            const cvBasedPackages = packageDetails.filter(pkg => pkg.cvLimiit > 0);
            setFilteredPackageDetails(cvBasedPackages);
        }
        else{
            const unlimitedPackages = packageDetails.filter(pkg => pkg.cvLimiit === 0);
            setFilteredPackageDetails(unlimitedPackages);
        }
    }

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        validateInput(name, value); 
    }

    const validateInput = (name: string, value: string) => {
        if (name === "telephone") {
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Telephone number is required"}}));
            } 
            else if (!validateTelephoneNumber(value)) {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Invalid telephone number!"}}));
            } 
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if (name === "tinNumber") {
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "TIN number is required"}}));
            }
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if (name === "billingAddress") {
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Billing address is required"}}));
            } 
            else if (!validateAddress(value)) {
                setValidation((values: any) => ({ ...values, [name]: { valid: false, invalid: true, invalidMessage: "Invalid billing address!" } }));
            }
            else {
                setValidation((values: any) => ({ ...values, [name]: { valid: true, invalid: false }}));
            }
        }
    }

    const validateForm = (): number => {
        const formValidation: PaymentDetailsValidation = {
            telephone: { valid: true },
            tinNumber: { valid: true },
            billingAddress: { valid: true } 
        }
        if (!checkout.telephone || checkout.telephone === "") {
            formValidation.telephone = { valid: false, invalid: true, invalidMessage: "Telephone number is required" };
        } 
        else if (!validateTelephoneNumber(checkout.telephone)) {
            formValidation.telephone = { valid: false, invalid: true, invalidMessage: "Invalid telephone number!" };
        }
        if (!checkout.tinNumber || checkout.tinNumber === "") {
            formValidation.tinNumber = { valid: false, invalid: true, invalidMessage: "TIN number is required" };
        }
        if (!checkout.billingAddress || checkout.billingAddress === "") {
            formValidation.billingAddress = { valid: false, invalid: true, invalidMessage: "Billing address is required" };
        } 
        else if (!validateAddress(checkout.billingAddress)) {
            formValidation.billingAddress = { valid: false, invalid: true, invalidMessage: "Invalid billing address!" };
        }
        setValidation(formValidation);
        return Object.keys(formValidation).filter(key => (formValidation as any)[key].valid).length;
    }

    async function handlePromoCodeApply(event: React.FormEvent<HTMLFormElement>) {   
        event.preventDefault();   
        if(recruiter === null) {
            handleStateUpdate({ isPromoInvalid: true, promoMessage: "Please Login to proceed" });
        } 
        else if (checkout.promoCode === "") {
            handleStateUpdate({ isPromoInvalid: true, promoMessage: "Promo Code is required" });
        } 
        else {
            await getPromoCodeValidation(recruiter.recruiterId, checkout.promoCode, state.checkoutType, recruiter!.token)
            .then((promoCode: PromoCode) => {
                if(promoCode) {  
                    const deduction = (basePrice * promoCode.discount) / 100;
                    setDiscountPercentage(promoCode.discount);
                    handleStateUpdate({ discount: deduction, isPromoInvalid: false });
                } 
                else {
                    handleStateUpdate({ discount: undefined, isPromoInvalid: true, promoMessage: "Invalid Promo Code. Try Again"})
                }
            })
            .catch((error) => {
                toErrorPage(error, UserType.Recruiter);            
            });
        }      
    }

    async function checkPendingInvoice() {
        if (recruiter != null) {
            await getRecruiterQuotaDetails(recruiter.recruiterId, recruiter.organizationId, recruiter.token)
            .then((recruiterQuotaDetails: any) => {
                if (recruiterQuotaDetails?.pendingInvoice) {
                    navigate(`/recruiter/invoice/pay/${recruiterQuotaDetails.pendingInvoice.refId}`);
                }
            })
            .catch((error) => {
                toErrorPage(error, UserType.Recruiter);
            });
        }
    }

    async function getPackageDetailList() {        
        setLoading(true);
        await getPackageDetails(recruiter!.organizationId)
        .then((response: any) => {
            const packageDetailLists: PackageDetail[] = response.packages;
            packageDetailLists.forEach((packageDetail) => {
                packageDetail.featureAvailability = Texts.featureAvailability.filter(x => x.packageId === packageDetail.packageId)[0].availability;
            });
            setPackageDetails(packageDetailLists);
            const cvBasedPackages = packageDetailLists.filter(pkg => pkg.cvLimiit > 0);
            setFilteredPackageDetails(cvBasedPackages);
            setCheckout((prev) => ({
                ...prev,
                telephone: response.organizationTelephone,
                tinNumber: response.organizationTINNumber,
                billingAddress: response.organizationAddress
            }));
            setLoading(false);
        })
        .catch((error) => {            
            toErrorPage(error, UserType.Recruiter);
        });
    }

    const handleProceedToCheckout = () => {
        if (!privacyPolicy) {
            openAlert("error", "Please agree to terms and conditions");
        } 
        else if (!checkoutState.selectedPackage) {
            openAlert("error", "Please select a package before proceeding");
        } 
        else {
            let validInputCount = Object.keys(validation).filter(key => (validation as any)[key].valid).length;
            if (validInputCount < 3) {
                validInputCount = validateForm();
            }
            if (validInputCount === 3) {
                handleCheckoutClick();
            }
        }
    };

    async function handleCheckoutClick() {
        checkout.checkoutType = CheckoutTypes.CVQuota;
        checkout.price = basePrice; 
        if(checkoutState.selectedPackage != null) {
            checkout.cvQuotaPackage = checkoutState.selectedPackage;
            checkout.selectedPackageId = checkoutState.selectedPackage.packageId;
        }
        checkout.noOfMonths = checkoutState.months;
        await postCheckoutRequest(checkout)
        .then((webXPayModel: WebXPayModel) => {
            $('.checkout #quotaForm').remove();
            $('.checkout').append('<form action="' + webXPayModel.checkoutUrl + '" id="quotaForm" name="form" method="post" style="display:none;">' +
                '<input type="text" name="first_name" value="' + webXPayModel.firstName + '" />' +
                '<input type="text" name="last_name" value="' + webXPayModel.lastName + '" />' +
                '<input type="text" name="email" value="' + webXPayModel.email + '" />' +
                '<input type="text" name="contact_number" value="' + webXPayModel.contactNumber + '" />' +
                '<input type="text" name="address_line_one" value="' + webXPayModel.address + '" />' +
                '<input type="text" name="process_currency" value="' + webXPayModel.currency + '" />' +
                '<input type="text" name="secret_key" value="' + webXPayModel.secretKey + '" />' +
                '<input type="text" name="custom_fields" value="' + webXPayModel.customFields + '" />' +
                '<input type="text" name="payment" value="' + webXPayModel.paymentStr + '" />' +
            '</form>');       
            var checkoutForm = document.getElementById("quotaForm") as HTMLFormElement;
            checkoutForm.submit();
        })
        .catch((error) => {
            toErrorPage(error, UserType.Recruiter);            
        });
    }

    return (
        <>
            <div style={styles.mainWrapper}>
                <div style={styles.leftWrapper}>
                    <RecruiterMenu>
                        <span style={styles.title}>{"Checkout"}</span>
                    </RecruiterMenu>
                </div>
                <div style={styles.rightWrapper}>
                    {isMobile ? <div style={styles.backButton.wrapper} onClick={() => navigate(-1)}>
                        <Button style={styles.backButton.button}><LeftArrow style={styles.backButton.icon} /></Button>
                        <span style={styles.backButton.text}>{"Back"}</span>
                    </div>
                    : <>
                        <div style={styles.buttonWrapper}>
                            <div style={styles.backButton.wrapper}>
                                <div style={styles.backButton.container} onClick={() => navigate(-1)}>
                                    <Button style={styles.backButton.button}><LeftArrow style={styles.backButton.icon} /></Button>
                                    <span style={styles.backButton.text}>{"Back"}</span>
                                </div>
                            </div>
                            <div style={styles.profileWrapper}><ProfileEntrance recruiter={recruiter} /></div>
                        </div>
                        <div style={styles.title}>{"Checkout"}</div>
                    </>}                    
                    <div style={styles.packages}>
                        <div style={styles.packages.subTitle}>{Texts.packages.subTitle}</div>
                        <div style={styles.packages.title}>
                            {Texts.packages.title.black}
                            <span style={styles.blue}>{Texts.packages.title.blue}</span>
                        </div>
                        <div style={styles.packages.packageTypeButton}>
                            <span style={styles.packages.packageTypeButton.cvBased}>{Texts.packagetypes.cvBased}</span>
                            <Switch checked={isUnlimited} styles={styles.packages.toggleButton} handleClick={() => {setIsUnlimited(!isUnlimited)}}/>
                            <span style={styles.packages.packageTypeButton.unlimited}>{Texts.packagetypes.unlimited}</span>
                        </div>  
                        {loading ? <TableLoader /> : <>
                            {isMobile ? <>
                                <Slider {...sliderSettings} ref={packageSliderRef}>            
                                    {filteredPackageDetails.map((packageDetail, index) => 
                                        <div key={index}>
                                            <div style={{...styles.packages.card, ...(packageDetail.packageId === hundredCVPackageId && styles.packages.card.popular)}}>
                                                {packageDetail.packageId === hundredCVPackageId && <div style={styles.packages.popular}>{"Most Popular"}</div>}
                                                <div style={styles.packages.cvLimit}>
                                                    {packageDetail.cvLimiit !== 0 ? "Up to " + packageDetail.cvLimiit + " CVs" : (packageDetail.price === 0 ? "Unlimited CVs" : "Unlimited CVs Per Month")}
                                                </div>
                                                <div style={styles.packages.price}>
                                                    {isMobile && <LeftChevron style={styles.packages.chevronLeftIcon} onClick={previousPackage}/>}
                                                    {(packageDetail.packageId !== annualPackageId) ? packageDetail.price + "LKR + VAT" : packageDetail.packageName}
                                                    {isMobile && 
                                                        <RightChevron 
                                                            style={{
                                                                ...styles.packages.chevronRightIcon, 
                                                                ...(packageDetail.packageId === annualPackageId && styles.packages.annualChevronRightIcon)
                                                            }} 
                                                            onClick={nextPackage}
                                                        />
                                                    }
                                                </div>
                                                {Texts.packageFeatures.map((feature, innerIndex) => 
                                                    <div 
                                                        key={innerIndex} 
                                                        style={{
                                                            ...styles.packages.featureEnable, 
                                                            ...(!packageDetail.featureAvailability[innerIndex] && styles.packages.featureDisable)
                                                        }}
                                                    >
                                                        {feature.feature}
                                                    </div>
                                                )}
                                                {(checkoutState.selectedPackage?.packageId === packageDetail.packageId) ? 
                                                    <Button style={styles.packages.selectedButton}>{"SELECTED"}</Button> 
                                                    : <Button 
                                                        style={styles.packages.button} 
                                                        onClick={() => handleSelectPackage(packageDetail)}
                                                    >
                                                        {packageDetail.packageId === annualPackageId ? "CONTACT US" : "SELECT"}
                                                    </Button>}
                                            </div>
                                        </div>                    
                                    )}                 
                                </Slider>
                            </>                 
                            : <> 
                                <table style={styles.packages.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.packages.included}><span style={styles.packages.tableHeading}>{"What’s Included"}</span></th>
                                            {filteredPackageDetails.map((packageDetail, index) => 
                                                <th 
                                                    key={index} 
                                                    style={{
                                                        ...styles.packages.tableHead, 
                                                        ...((hoveredIndex === index) && styles.packages.tableHead.hovered), 
                                                        ...((checkoutState.selectedPackage?.packageId === packageDetail.packageId) && styles.packages.selectedPackage), 
                                                        ...(!(hoveredIndex === index) && (packageDetail.packageId === hundredCVPackageId) && styles.packages.headBorderBlack),
                                                        ...((hoveredIndex !== -1 && hoveredIndex === index - 1) && styles.packages.tableHead.nextColumnHovered),
                                                        ...((hoveredIndex !== -1 && hoveredIndex === index + 1) && styles.packages.tableHead.prevColumnHovered)
                                                    }} 
                                                    onMouseEnter={() => setHoveredIndex(index)} 
                                                    onMouseLeave={() => setHoveredIndex(-1)}
                                                >
                                                    <div 
                                                        style={{
                                                            ...styles.packages.cvLimit, 
                                                            ...(packageDetail.packageId === hundredCVPackageId && styles.packages.textBlue), 
                                                            ...((packageDetail.packageId === unlimitedCVPackageId || packageDetail.packageId === annualPackageId) && styles.packages.textRed)
                                                        }}
                                                    >
                                                        {packageDetail.cvLimiit !== 0 ? "Up to " + packageDetail.cvLimiit + " CVs" : (packageDetail.price === 0 ? "Unlimited CVs" : "Unlimited CVs Per Month")}
                                                    </div>
                                                    <div 
                                                        style={{
                                                            ...styles.packages.price, 
                                                            ...(packageDetail.packageId === hundredCVPackageId && styles.packages.textBlue), 
                                                            ...((packageDetail.packageId === unlimitedCVPackageId || packageDetail.packageId === annualPackageId) && styles.packages.textRed),
                                                            ...(packageDetail.cvLimiit === 0 && styles.packages.unlimitedCVPrice),
                                                            ...(packageDetail.packageId === annualPackageId && styles.packages.annualPackageName)
                                                        }}
                                                    >
                                                        {(packageDetail.packageId !== annualPackageId) ? packageDetail.price : packageDetail.packageName}
                                                        {(packageDetail.packageId !== annualPackageId) && 
                                                        <span 
                                                            style={{
                                                                ...styles.packages.lkr, 
                                                                ...(packageDetail.packageId === hundredCVPackageId && styles.packages.textBlue),
                                                                ...(packageDetail.cvLimiit === 0 && styles.packages.unlimitedCVPrice), 
                                                                ...((packageDetail.packageId === unlimitedCVPackageId || packageDetail.packageId === annualPackageId) && styles.packages.textRed)
                                                            }}
                                                        >
                                                            {"LKR + VAT"}
                                                        </span>}
                                                    </div>
                                                    {(hoveredIndex !== index) && packageDetail.packageId === hundredCVPackageId && <div style={styles.packages.popular}>{"Most Popular"}</div>}                                    
                                                    {(hoveredIndex === index) && (checkoutState.selectedPackage?.packageId === packageDetail.packageId ? 
                                                        (<Button style={styles.packages.selectedButton}>{"SELECTED"}</Button>)
                                                        : (<Button 
                                                            style={{
                                                                ...styles.packages.button, 
                                                                ...(packageDetail.cvLimiit === 0 && styles.packages.unlimitedCVButton),
                                                                ...(packageDetail.cvLimiit === 0 && packageDetail.price === 0 && styles.packages.annualCVButton)
                                                            }} 
                                                            onClick={() => handleSelectPackage(packageDetail)}
                                                        >
                                                            {packageDetail.packageId === annualPackageId ? "CONTACT US" : "SELECT"}
                                                        </Button>)
                                                    )}
                                                </th>                    
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Texts.packageFeatures.map((feature, rowIndex) => 
                                            <tr key={rowIndex}>
                                                <td style={styles.packages.body}>
                                                    <span style={styles.packages.featureEnable}>{feature.feature}</span>
                                                </td>
                                                {filteredPackageDetails.map((packageDetail, cellIndex) => 
                                                    <td 
                                                        key={cellIndex} 
                                                        style={{
                                                            ...styles.packages.cellWrapper, 
                                                            ...((hoveredIndex === cellIndex) && styles.packages.cellWrapper.hovered), 
                                                            ...((hoveredIndex === cellIndex) && (rowIndex === 0) && styles.packages.table.firstRow), 
                                                            ...((hoveredIndex === cellIndex) && (rowIndex === 14) && styles.packages.table.lastRow), 
                                                            ...((checkoutState.selectedPackage?.packageId === packageDetail.packageId) && styles.packages.selectedPackage),
                                                            ...(!(hoveredIndex === cellIndex) && (packageDetail.packageId === hundredCVPackageId) && styles.packages.bodyBorderBlack),
                                                            ...((hoveredIndex !== -1 && hoveredIndex === cellIndex - 1) && styles.packages.cellWrapper.nextColumnHovered),
                                                            ...((hoveredIndex !== -1 && hoveredIndex === cellIndex + 1) && styles.packages.cellWrapper.prevColumnHovered)
                                                        }} 
                                                        onMouseEnter={() => setHoveredIndex(cellIndex)} 
                                                        onMouseLeave={() => setHoveredIndex(-1)}
                                                    >
                                                        {packageDetail.featureAvailability[rowIndex] ? <CheckCircle style={styles.packages.enableIcon} /> : <CheckCircleDisabled style={styles.packages.disableIcon} />}
                                                    </td>
                                                )}
                                            </tr>
                                        )}
                                    </tbody>
                                </table>                                                          
                            </>}
                        </>} 
                    </div>
                    <div style={styles.subscription}>
                        <div ref={subscriptionRef} style={{ height: '40px', visibility: 'hidden' }} />
                        <div style={styles.subscription.title}>{"Subscription"}</div>
                        <div style={styles.subscription.packageWrapper}>
                            {checkoutState.selectedPackage ? <>
                                <div style={styles.subscription.monthWrapper}>
                                    <div style={styles.subscription.unlimited}>
                                        {checkoutState.selectedPackage && checkoutState.selectedPackage.cvLimiit > 0 ? checkoutState.selectedPackage.packageName : "Unlimited CVs a month"}
                                    </div>
                                    {isUnlimited && (checkoutState.selectedPackage && checkoutState.selectedPackage.cvLimiit == 0) && 
                                        <SingleSelect styles={styles.subscription.monthsCountSelect} selectInputs={monthsCountSelectInputs} getSelected={handleMonthsChange} />}
                                    <div style={styles.subscription.priceWrapper}>
                                        <div style={styles.subscription.price}>
                                            {!checkoutState.selectedPackage 
                                                ? 0 : checkoutState.selectedPackage.packageId === unlimitedCVPackageId 
                                                    ? (checkoutState.selectedPackage.price * checkoutState.months): checkoutState.selectedPackage.price
                                            } LKR
                                        </div>
                                        <CloseCircle style={styles.subscription.closeButton} onClick={() => handleStateUpdate({ selectedPackage: null, discount: undefined })}/>
                                    </div>
                                </div>
                            </> : <EmptyCart style={styles.emptyCart}/>}
                            {checkoutState.selectedPackage && 
                            <div 
                                style={{
                                    ...styles.subscription.promoWrapper, 
                                    ...(checkoutState.isPromoInvalid && !checkoutState.discount && (styles.subscription.promoInvalid))
                                }}
                            > 
                                <div style={styles.subscription.promoCode}>
                                    <div style={{...styles.subscription.subTitle, ...styles.subscription.promoTitle}}>{"Have a Promo Code?"}</div>
                                    <div style={styles.subscription.inputWrapper}>
                                        <TextInput
                                            inputType={'text'}
                                            name={'promoCode'}
                                            value={checkout.promoCode}
                                            handleChange={handleInputChange}
                                            styles={styles.subscription.inputCode}
                                            disabled={checkoutState.discount !== undefined}
                                            placeHolder={'XXXX XXXXX XXXXXXXXX'}
                                        />
                                        <Button 
                                            style={{...styles.subscription.button, ...(checkoutState.discount && styles.subscription.button.applied)}} 
                                            onClick={handlePromoCodeApply} 
                                            disabled={checkoutState.discount !== undefined}
                                        >
                                            {checkoutState.discount ? "APPLIED" : "APPLY"}
                                        </Button>
                                    </div>
                                </div>
                                {checkoutState.isPromoInvalid && !checkoutState.discount && <div style={styles.subscription.promoInvalidMessage}>{checkoutState.promoMessage}</div>}
                            </div>}
                        </div>                         
                    </div>
                    <div style={styles.checkout}>
                        <hr style={styles.checkout.line}/>                
                        <p style={styles.checkout.priceRow}>Sub Total <span style={styles.checkout.priceValue}>{basePrice} LKR</span></p>
                        {checkoutState.discount && <>
                            <p style={styles.checkout.priceRow}>Discount <span style={styles.checkout.priceValue}>{-checkoutState.discount} LKR</span></p>
                        </>}
                        {basePrice > 0 && <>
                            <p style={styles.checkout.priceRow}>{`VAT (${process.env.REACT_APP_VAT_RATE}%)`} <span style={styles.checkout.priceValue}>{parseFloat(vatAmount.toFixed(2))} LKR</span></p>
                            <p style={styles.checkout.priceRow}>Gross Total <span style={styles.checkout.priceValue}>{parseFloat(price.toFixed(2))} LKR</span></p>
                        </>}
                        <CheckboxInput name="PrivacyPolicy" value={privacyPolicy} handleChange={() => setPrivacyPolicy(prev => !prev)} styles={styles.checkboxInput}>
                            <span 
                                style={{
                                    ...styles.checkout.policyLabel, 
                                    ...(styles.checkout.sharpText), 
                                    ...(!privacyPolicy && styles.checkout.redText)
                                }}
                            >
                                I accept the 
                                <span 
                                    style={{...styles.checkout.sharpText.black, ...(!privacyPolicy && styles.checkout.redText)}} 
                                    onClick={handleTermsClick}
                                > 
                                    Terms/Conditions.
                                </span>
                            </span>
                        </CheckboxInput>
                        <div style={styles.inputContainer}>
                            <TextInput
                                inputType={'text'}
                                name={'telephone'}
                                value={checkout.telephone}
                                label={"Telephone Number *"}
                                handleChange={handleInputChange}
                                handleBlur={handleInputBlur}
                                styles={styles.textInput}
                                validation={validation.telephone}
                            />
                            <TextInput
                                inputType={'text'}
                                name={'tinNumber'}
                                value={checkout.tinNumber}
                                label={'TIN Number *'}
                                handleChange={handleInputChange}
                                handleBlur={handleInputBlur}
                                styles={styles.textInput}
                                validation={validation.tinNumber}
                            />
                            <TextInput
                                inputType={'text'}
                                name={'billingAddress'}
                                value={checkout.billingAddress}
                                label={'Address *'}
                                handleChange={handleInputChange}
                                handleBlur={handleInputBlur}
                                styles={styles.textInput}
                                validation={validation.billingAddress}
                            />
                        </div>
                        <Button type="button" style={styles.checkout.checkoutButton} onClick={handleProceedToCheckout}>{"PROCEED TO CHECKOUT"}</Button> 
                        <span style={styles.checkout.orLable}>Or</span>   
                        <Button type="button" style={styles.checkout.depositButton} onClick={toDeposit}>{"BANK DEPOSIT"}</Button>
                        <div style={styles.checkout.iconGride}>
                            {PaymentIcons.map((paymentIcon, index) =>                    
                                <img key={index} src={paymentIcon} style={styles.checkout.paymentIcon}/>                    
                            )}
                        </div>               
                    </div>
                    <div className="checkout"></div>
                </div>
            </div>
            <PopUpWindow styles={styles.dialog} open={openMessageDialog} toggle={() => setOpenMessageDialog(prev => !prev)} closeSlice={false} closeCircle={true}>
                {dialogContent}
            </PopUpWindow>
            <Alert/>
            <Footer/>
        </>
    )
}